import { Controller } from "@hotwired/stimulus"

// Expand/collapse for the sidebar's project rows, remembered somewhere the SERVER can read.
//
// Close cousin of `disclosure`, but deliberately not the same controller, for two reasons:
//
// 1. This state has to survive a reload as a *first paint*, not as a correction applied once JS
//    connects — the rail used to open every project collapsed and snap the remembered ones open a
//    moment later. Only a cookie is visible to both ends (see ApplicationHelper#sidebar_project_expanded?),
//    and it is unsigned because this writes it without a round trip and JS cannot produce a
//    signature Rails would accept.
// 2. The cookie is ONE shared list of open project ids, so it has one owner: this controller sits
//    on the <ul>, not on each row. N per-row instances read-modify-writing the same value is the
//    racy version of this. It also means a row prepended by create.turbo_stream.erb is wired
//    already, with no connect() of its own.
//
// The DOM is the truth and the cookie is its mirror — the same posture sidebar_collapse takes.
// Rewriting the whole list from the DOM on every toggle also prunes the id of a project that has
// since been deleted, since only rendered rows can contribute one.

// A year, because a preference that quietly resets is worse than none.
const COOKIE_MAX_AGE_SECONDS = 31536000

export default class extends Controller {
  static values = { cookie: String }

  toggle(event) {
    const row = event.target.closest(".sidebar__project")
    if (!row) return

    const children = row.querySelector(".sidebar__project-children")
    const open = children.hidden

    children.hidden = !open
    row.classList.toggle("is-open", open)

    const button = row.querySelector("[aria-expanded]")
    if (button) button.setAttribute("aria-expanded", open ? "true" : "false")

    this.#persist()

    // .sidebar__nav scrolls, so opening the last project in a long list would otherwise reveal its
    // rows below the fold with nothing to suggest they had appeared. Pull the whole revealed block
    // in, not just its first row — the same reason `disclosure` does this for a request type's
    // editor.
    if (open) children.scrollIntoView({ block: "nearest", behavior: "instant" })
  }

  #persist() {
    const secure = location.protocol === "https:" ? "; Secure" : ""

    document.cookie =
      `${this.cookieValue}=${this.#openProjectIds.join(".")}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  }

  // Only the open ids: closed is the default the server assumes, so a closed project is the
  // absence of an id rather than an entry that accumulates.
  get #openProjectIds() {
    return Array.from(this.element.querySelectorAll(".sidebar__project.is-open"))
      .map((row) => row.dataset.projectId)
      .filter(Boolean)
  }
}
